import { useMemo } from "react";

import { Button, Grid, Stack } from "@mui/material";

import { ResetFiltersButton } from "components/core/ResetFiltersButton.tsx";
import { EquipmentTypeMultiSelect } from "components/EquipmentTypeSelect";
import { ProvinceStateMultiSelect } from "components/ProvinceStateMultiSelect";
import { ShipmentServiceMultiSelect } from "components/ShipmentServiceMultiSelect";
import { ShipmentStatusMultiSelect } from "components/ShipmentStatusSelect";
import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";

import { ShipmentSearchByName } from "../../ShipmentsFilter/components/ShipmentSearchByName";
import { DEFAULT_SUPPLIER_SHIPMENT_STATUSES } from "../../ShipmentsFilter/constants";

interface Props {
  isOpen: boolean;
  onOpen: VoidFunction;
  onClose: VoidFunction;
}

export function SupplierShipmentsFilter(props: Props) {
  const { isOpen, onOpen, onClose } = props;

  const {
    updateOrigin,
    updateDestination,
    updateEquipmentType,
    updateServiceType,
    updateShipmentStatus,
    filters,
    clearFilters,
    refreshShipments,
  } = useShipmentFiltersContext("supplier");

  const filterState = useMemo(
    () => ({
      equipmentTypes: filters?.equipment_types ?? [],
      services: filters?.services ?? [],
      shipmentStatus: filters?.status ?? DEFAULT_SUPPLIER_SHIPMENT_STATUSES,
      origin: {
        provinces: filters?.origin?.provinces ?? [],
      },
      destination: {
        provinces: filters?.destination?.provinces ?? [],
      },
    }),
    [filters],
  );

  const handleLocationChange = (
    key: keyof Location,
    addressKey: keyof ShipmentLocationFilter,
    value: string[],
  ): void => {
    if (key === "origin") {
      updateOrigin(addressKey, value);
    } else if (key === "destination") {
      updateDestination(addressKey, value);
    }
  };

  const handleEquipmentTypeChange = (value: string[]): void => {
    updateEquipmentType(value);
  };

  const handleServiceTypeChange = (value: string[]): void => {
    updateServiceType(value);
  };

  const handleShipmentStatusChange = (value: string[]): void => {
    updateShipmentStatus(value as ShipmentStatusValue[]);
  };

  const handleClearFilters = (): void => {
    clearFilters();
    updateShipmentStatus(DEFAULT_SUPPLIER_SHIPMENT_STATUSES);
    refreshShipments();
  };

  const toggleFilterTools = (): void => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column" spacing={2}>
        <Grid container spacing={1} pt={1}>
          <Grid item xs={12} md={6}>
            <ShipmentSearchByName tabType="supplier" sx={{ width: "100%" }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ShipmentStatusMultiSelect
              value={filterState.shipmentStatus}
              onChange={(newValue) => handleShipmentStatusChange(newValue)}
              excludeStatuses={["ACTIVE", "CANCELLED", "EXPIRED"]}
              width="100%"
            />
          </Grid>
          {isOpen && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <EquipmentTypeMultiSelect
                  onChange={handleEquipmentTypeChange}
                  defaultValue={filterState.equipmentTypes}
                  value={filterState.equipmentTypes}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ShipmentServiceMultiSelect
                  label="Services"
                  value={filterState.services}
                  onChange={(newValue) => handleServiceTypeChange(newValue)}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ProvinceStateMultiSelect
                  label="Origins"
                  value={filterState.origin.provinces}
                  onChange={(value) =>
                    handleLocationChange("origin", "provinces", value)
                  }
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ProvinceStateMultiSelect
                  label="Destinations"
                  value={filterState.destination.provinces}
                  onChange={(value) =>
                    handleLocationChange("destination", "provinces", value)
                  }
                  width="100%"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        paddingLeft={1}
      >
        <Button variant="outlined" onClick={() => toggleFilterTools()}>
          {isOpen ? "Less" : "More"}
        </Button>
        <ResetFiltersButton handleResetFilters={handleClearFilters} />
      </Stack>
    </Stack>
  );
}
